/**
 * Fleet Simulation - Groundfish: session picker and Play/Cancel buttons.
 * FleetAnimationControls is rendered by DataLayerTabs (visible DOM) so the animation runs without throttling.
 * Auto-plays when provided in layout as initial layer (skipped when play-scallop-simulation is also in layout).
 */
import React, { useEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import BetaChip from '../../../components/common/BetaChip/BetaChip';

interface SessionItem {
  session_id: string;
  session_name?: string;
  scenario_name?: string;
  simulation_year?: number;
  created_date?: string;
}

interface GroundfishInjections {
  availableSessions?: SessionItem[];
  selectedSessionId?: string | null;
  onSessionChange?: (sessionId: string) => void;
  onFleetAnimationPlay?: () => void;
  onFleetAnimationCancel?: () => void;
  fleetMapData?: unknown[];
  fleetDataLoading?: boolean;
  initialLayerSlugs?: string[];
}

export default function PlayGroundfishSimulationDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { injections = {}, componentSlug } = props;
  const {
    availableSessions = [],
    selectedSessionId = null,
    onSessionChange = () => {},
    onFleetAnimationPlay = () => {},
    onFleetAnimationCancel = () => {},
    fleetMapData = [],
    fleetDataLoading = false,
    initialLayerSlugs = [],
  } = injections as GroundfishInjections;
  const autoPlayTriggeredRef = useRef(false);
  const isInInitialLayers = initialLayerSlugs.includes(componentSlug);

  // When present in bottom panel active-data-layers, auto-select first session and play when data is ready.
  useEffect(() => {
    if (!isInInitialLayers) return;
    if (autoPlayTriggeredRef.current) return;
    if (fleetDataLoading) return;

    const sessionId = selectedSessionId || availableSessions[0]?.session_id;
    if (!sessionId) return;

    // Select first session if none selected yet
    if (!selectedSessionId) {
      onSessionChange(sessionId);
      return;
    }

    // Wait for fleet data to load before playing
    if (fleetMapData.length === 0) return;

    onFleetAnimationPlay();
    autoPlayTriggeredRef.current = true;
  }, [
    isInInitialLayers,
    availableSessions,
    selectedSessionId,
    fleetMapData.length,
    fleetDataLoading,
    onSessionChange,
    onFleetAnimationPlay,
  ]);

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">
          Fleet Simulation - Groundfish <BetaChip />
        </span>
      </div>
      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '18px',
          fontStyle: 'italic',
        }}
      >
        Visualize fishing fleet movements
      </div>

      <div className="form-group" style={{ marginBottom: '15px' }}>
        <label className="form-label" htmlFor="sessionSelect">
          Simulation Session
        </label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            id="sessionSelect"
            className="form-control"
            value={selectedSessionId || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              onSessionChange(e.target.value)
            }
            disabled={fleetDataLoading}
            style={{ flex: 1 }}
          >
            <option value="">
              {fleetDataLoading ? 'Loading sessions...' : 'Select a session...'}
            </option>
            {availableSessions.map((session) => (
              <option key={session.session_id} value={session.session_id}>
                {session.session_name || session.scenario_name || session.session_id}
                {session.simulation_year && ` (${session.simulation_year})`}
                {session.created_date &&
                  ` - ${new Date(session.created_date).toLocaleDateString()}`}
              </option>
            ))}
          </select>

          <button
            onClick={onFleetAnimationPlay}
            disabled={
              fleetDataLoading ||
              !selectedSessionId ||
              fleetMapData.length === 0
            }
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor:
                fleetDataLoading ||
                !selectedSessionId ||
                fleetMapData.length === 0
                  ? 'not-allowed'
                  : 'pointer',
              opacity:
                fleetDataLoading ||
                !selectedSessionId ||
                fleetMapData.length === 0
                  ? 0.5
                  : 1,
              fontWeight: '500',
              maxWidth: '35px',
            }}
          >
            ▶
          </button>

          <button
            onClick={onFleetAnimationCancel}
            disabled={
              fleetDataLoading ||
              !selectedSessionId ||
              fleetMapData.length === 0
            }
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor:
                fleetDataLoading ||
                !selectedSessionId ||
                fleetMapData.length === 0
                  ? 'not-allowed'
                  : 'pointer',
              opacity:
                fleetDataLoading ||
                !selectedSessionId ||
                fleetMapData.length === 0
                  ? 0.5
                  : 1,
              fontWeight: '1000',
              maxWidth: '35px',
            }}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
